/**
 * "Connect using" of the Azure Storage form (spec §4.1; Option C, C2: the first row of the Storage section, because it
 * decides every row after it): storage account · emulator (Azurite) · single resource, as a segmented control of NATIVE
 * radios (`storage-mode-*`, checked semantics kept — rule 3b.3). Each radio covers its whole segment, so a click or
 * Playwright's `check()` lands on the input itself; arrow keys move between them as in any radio group.
 *
 * The two passive lines that belong to a mode sit under the Storage account / Resource URL field: what the
 * single-resource attachment points at, and the copyable `docker run` command for the emulator (testing the preset
 * never starts Docker).
 */
import { Fragment } from 'react';
import { Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { StorageAttachmentScope } from '@/types/azureStorage';
import { describeStorageResource, type StorageConnectMode } from './azureStorageFormRules';

/** Short labels, so the three fit one row from a 936-px window (plan §3.4); "Single resource" is the guidance's word. */
const MODES: ReadonlyArray<{ id: StorageConnectMode; label: string }> = [
  { id: 'account', label: 'Storage account' },
  { id: 'emulator', label: 'Emulator (Azurite)' },
  { id: 'resource', label: 'Single resource' },
];

const ANONYMOUS_LOCK =
  'Anonymous access works on one public container or blob — listing an account is never anonymous';

type ModeProps = {
  mode: StorageConnectMode;
  /** Anonymous is a single-resource attachment by construction — the other modes lock. */
  anonymous: boolean;
  onPick: (mode: StorageConnectMode) => void;
};

export function AzureStorageModeFields({ mode, anonymous, onPick }: ModeProps) {
  return (
    <div className="inline-flex max-w-full flex-wrap gap-0.5 rounded-md border border-input bg-muted/50 p-0.5" data-testid="storage-connect-mode">
      {MODES.map((option) => {
        const locked = anonymous && option.id !== 'resource';
        const checked = mode === option.id;
        return (
          <label
            key={option.id}
            className={cn(
              'relative flex h-8 items-center whitespace-nowrap rounded-[5px] px-2.5 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
              checked ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground',
              locked && 'cursor-not-allowed opacity-50',
            )}
          >
            <input
              type="radio"
              name="storage-connect-mode"
              className="absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-[5px] opacity-0 disabled:cursor-not-allowed"
              checked={checked}
              disabled={locked}
              title={locked ? ANONYMOUS_LOCK : undefined}
              onChange={() => onPick(option.id)}
              data-testid={`storage-mode-${option.id}`}
            />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}

type ModeHintProps = {
  mode: StorageConnectMode;
  scope: StorageAttachmentScope | undefined;
  /** The emulator's `docker run` command in pieces (`azuriteDockerRunParts`, from the form's endpoints). */
  emulatorCommand: readonly string[];
  onCopyEmulatorCommand: () => void;
};

/** The mode's line under the Storage account / Resource URL field. */
export function AzureStorageModeHint({ mode, scope, emulatorCommand, onCopyEmulatorCommand }: ModeHintProps) {
  if (mode === 'resource') {
    return (
      <p className="mt-1 break-words text-xs text-muted-foreground" data-testid="storage-resource-summary">
        {scope?.kind === 'resource'
          ? `Attached to ${describeStorageResource(scope.resource)}.`
          : 'Paste a container, blob, table, queue or share URL, with or without a SAS.'}
      </p>
    );
  }
  if (mode !== 'emulator') return null;
  return (
    <div className="mt-2 flex items-start justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2">
      <p className="min-w-0 text-xs text-muted-foreground" data-testid="storage-azurite-hint">
        Not running yet? Start Azurite locally — testing this connection never starts Docker.
        {/* Each piece (`-p 20000:10000`) is one inline block, so a narrow form breaks the line between pieces, never inside
            a port number; only a piece wider than the line (the image name) breaks within itself. */}
        <span className="mt-1 block font-mono text-[11px]" data-testid="storage-azurite-command">
          {emulatorCommand.map((part, index) => (
            <Fragment key={index}>
              {index > 0 && ' '}
              <span className="inline-block max-w-full [overflow-wrap:anywhere]" data-testid="storage-azurite-command-part">
                {part}
              </span>
            </Fragment>
          ))}
        </span>
      </p>
      <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={onCopyEmulatorCommand} data-testid="storage-azurite-copy">
        <Copy className="h-3.5 w-3.5" />
        Copy
      </Button>
    </div>
  );
}
